// src/Dashboard/pages/user/Messaging/components/PrivateMessagesPanel.jsx

import React, { useState } from "react";
import {
  FaCheckDouble,
  FaEnvelope,
  FaEye,
  FaReply,
  FaTrash,
} from "react-icons/fa";

import {
  formatMessageDate,
  formatMessageTime,
  getMessageDateValue,
  getUserFullName,
  isUnreadMessage,
  sanitizeLight,
} from "../utils/messagingUtils";

function MessageContentPreview({
  content = "",
  expanded = false,
  onToggle,
  collapsedClassName = "max-h-32",
}) {
  const safeContent = sanitizeLight(content || "");

  return (
    <article className="relative mt-3 rounded-[20px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
      <div
        className={[
          "prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200",
          "prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1",
          "prose-a:text-[#013E8E] dark:prose-a:text-indigo-300",
          expanded ? "" : `${collapsedClassName} overflow-hidden`,
        ].join(" ")}
        dangerouslySetInnerHTML={{
          __html:
            safeContent || '<p class="italic text-slate-500">Message vide.</p>',
        }}
      />

      {!expanded && safeContent && (
        <div className="pointer-events-none absolute inset-x-4 bottom-12 h-12 bg-gradient-to-t from-white to-transparent dark:from-slate-950" />
      )}

      {safeContent && (
        <button
          type="button"
          onClick={onToggle}
          className="relative mt-3 inline-flex items-center rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {expanded ? "Réduire" : "Lire la suite"}
        </button>
      )}
    </article>
  );
}

export default function PrivateMessagesPanel({
  type = "received",
  title,
  subtitle,
  messages = [],
  loading,
  unreadCount = 0,
  busyAllRead,
  onOpen,
  onReply,
  onDelete,
  onMarkRead,
  onMarkUnread,
  onMarkAllRead,
}) {
  const isReceived = type === "received";
  const [expandedMessages, setExpandedMessages] = useState({});

  const toggleMessagePreview = (messageId) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Messagerie privée
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            {subtitle && (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {subtitle}
              </p>
            )}
          </div>

          {isReceived && (
            <button
              type="button"
              onClick={onMarkAllRead}
              disabled={busyAllRead || unreadCount === 0}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              <FaCheckDouble />
              Tout marquer lu
            </button>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-20 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center dark:border-slate-700 dark:bg-slate-800/40">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
              <FaEnvelope className="text-2xl" />
            </div>

            <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
              Aucun message
            </h3>

            <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
              Aucun message à afficher pour le moment.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden overflow-hidden rounded-[24px] border border-slate-200 dark:border-slate-800 md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                  <thead className="bg-slate-50 dark:bg-slate-950">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Message
                      </th>

                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {messages.map((message) => {
                      const isUnread = isUnreadMessage(message);
                      const contact = isReceived
                        ? message.Sender
                        : message.Recipient;

                      return (
                        <tr
                          key={message.id}
                          className={`transition hover:bg-slate-50 dark:hover:bg-slate-950 ${
                            isUnread && isReceived
                              ? "bg-blue-50/70 dark:bg-blue-500/10"
                              : "bg-white dark:bg-slate-900"
                          }`}
                        >
                          <td className="whitespace-nowrap px-4 py-4 align-top">
                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                              {formatMessageDate(getMessageDateValue(message))}
                            </p>

                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {formatMessageTime(getMessageDateValue(message))}
                            </p>
                          </td>

                          <td className="px-4 py-4 align-top">
                            <div className="flex items-start gap-2">
                              {isUnread && isReceived && (
                                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-600" />
                              )}

                              <div className="min-w-0">
                                <p className="font-bold text-slate-900 dark:text-white">
                                  {getUserFullName(contact)}
                                </p>

                                <p className="mt-1 font-bold text-[#013E8E] dark:text-indigo-300">
                                  {message.subject || "Sans objet"}
                                </p>

                                <MessageContentPreview
                                  content={message.content || ""}
                                  expanded={!!expandedMessages[message.id]}
                                  onToggle={() =>
                                    toggleMessagePreview(message.id)
                                  }
                                  collapsedClassName="max-h-40"
                                />
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-4 align-top">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => onOpen(message)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                                title="Lire"
                              >
                                <FaEye />
                              </button>

                              {isReceived && (
                                <button
                                  type="button"
                                  onClick={() => onReply?.(message)}
                                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 transition hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                                  title="Répondre"
                                >
                                  <FaReply />
                                </button>
                              )}

                              {isReceived && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    isUnread
                                      ? onMarkRead?.(message.id)
                                      : onMarkUnread?.(message.id)
                                  }
                                  className="inline-flex h-10 items-center justify-center rounded-2xl bg-emerald-50 px-3 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                                  title={
                                    isUnread
                                      ? "Marquer comme lu"
                                      : "Marquer comme non lu"
                                  }
                                >
                                  {isUnread ? "Lu" : "Non lu"}
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => onDelete(message.id)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                                title="Supprimer"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile */}
            <div className="space-y-3 md:hidden">
              {messages.map((message) => {
                const isUnread = isUnreadMessage(message);
                const contact = isReceived ? message.Sender : message.Recipient;

                return (
                  <article
                    key={message.id}
                    className={`rounded-[24px] border p-4 shadow-sm ${
                      isUnread && isReceived
                        ? "border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10"
                        : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">
                          {getUserFullName(contact)}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {formatMessageDate(getMessageDateValue(message))} à{" "}
                          {formatMessageTime(getMessageDateValue(message))}
                        </p>
                      </div>

                      {isUnread && isReceived && (
                        <span className="rounded-full bg-blue-600 px-2 py-1 text-[10px] font-bold uppercase text-white">
                          Nouveau
                        </span>
                      )}
                    </div>

                    <p className="mt-3 font-bold text-[#013E8E] dark:text-indigo-300">
                      {message.subject || "Sans objet"}
                    </p>

                    <MessageContentPreview
                      content={message.content || ""}
                      expanded={!!expandedMessages[message.id]}
                      onToggle={() => toggleMessagePreview(message.id)}
                      collapsedClassName="max-h-40"
                    />

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onOpen(message)}
                        className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-xs font-bold text-white"
                      >
                        <FaEye />
                        Lire
                      </button>

                      {isReceived && (
                        <button
                          type="button"
                          onClick={() => onReply?.(message)}
                          className="inline-flex items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                        >
                          <FaReply />
                          Répondre
                        </button>
                      )}

                      {isReceived && (
                        <button
                          type="button"
                          onClick={() =>
                            isUnread
                              ? onMarkRead?.(message.id)
                              : onMarkUnread?.(message.id)
                          }
                          className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                        >
                          {isUnread ? "Marquer lu" : "Marquer non lu"}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => onDelete(message.id)}
                        className="inline-flex items-center gap-2 rounded-2xl bg-red-50 px-3 py-2 text-xs font-bold text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                      >
                        <FaTrash />
                        Supprimer
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
